<template>
  <div ref="paginated-table">
    <div
      class="mb-4"
      v-if="!isNoData && !firstLoad && $slots['table-controls']"
    >
      <slot name="table-controls"></slot>
    </div>
    <slot name="no-data" v-if="isNoData"> </slot>
    <slot name="no-data-for-query" v-if="isNoDataForQuery"> </slot>
    <slot :data="{ res }" v-if="!isNoDataForQuery && !isNoData && !firstLoad">
      <div class="d-flex table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th scope="col" v-for="field in fields" :key="field.name">
                <HMIcon
                  :icon="getOrderingIcons(field.ordering)"
                  class="ordering"
                  :class="{
                    'ordering--selected': field.ordering === ordering,
                  }"
                  v-if="field.ordering"
                  @click="orderingBy(field.ordering)"
                ></HMIcon>
                {{ field.title }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              @click="$emit('item-click', rowData[0].slotData)"
              v-for="(rowData, rowIndex) in tableData"
              :key="rowIndex"
              :class="{
                'is-hover': hoverIndex === rowIndex && !disableHover,
                pointer: rowsClickable,
              }"
            >
              <td
                @click="
                  $event => cellData.disableClick && $event.stopPropagation()
                "
                @mouseenter="setHoverIndex(rowIndex, cellData)"
                @mouseleave="hoverIndex = null"
                :class="getCssClass(cellData)"
                v-for="(cellData, cellIndex) in rowData"
                :key="cellIndex"
              >
                <slot :name="cellData.slotName" :data="cellData.slotData">
                  {{ cellData.text }}
                </slot>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </slot>
    <div id="pagination" class="mt-5" v-if="showPagination">
      <hr />
      <HMPagination
        :total-rows="res.count"
        :per-page="perPage"
        :max-pages-shown="paginationLimit"
        v-model="currentPageValue"
        :show-pagination-buttons="showPaginationButtons"
      ></HMPagination>
    </div>
  </div>
</template>

<script lang="ts">
import { throttle, get } from 'lodash-es';
import { isEmpty, values } from 'lodash-es';

interface Field {
  // use name when you need to use the slot
  name?: string;
  title?: string;
  // use key when you just want to render the text value of object property
  key?: string;
  cssClass?: string;
  ordering?: string;
  // don't call item-click event on td
  disableClick?: boolean;
}

const emptyResponse: PaginatedResponse<any> = {
  count: 0,
  results: [],
};


export default {
  props: {
    name: {
      type: String,
      required: true,
    },
    fields: {
      type: Array as () => Array<Field>,
      default() {
        return [];
      },
    },
    api: {
      type: Function,
      default: null,
    },
    query: {
      type: Object,
      default() {
        return {};
      },
    },
    rowsClickable: {
      type: Boolean,
      default: false,
    },
    showPaginationButtons: {
      type: Boolean,
      default: true,
    },
    pageInQuery: {
      type: Boolean,
      default: false,
    },
    perPageFixed: {
      type: Number,
      required: false,
    },
    disableHover: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      paginationLimit: 1,
      firstLoad: true,
      hoverIndex: null,

      isLoading: true,
      perPage: 0,
      ordering: null,
      orderingDirection: '',
      currentPage: undefined,
      allPages: 0,
      res: emptyResponse,
    };
  },
  methods: {
    getOrderingIcons(ordering) {
      switch (this.orderingDirection + ordering) {
        case this.ordering:
          return ['fas', 'fa-sort-up'];
        case `-${this.ordering}`:
          return ['fas', 'fa-sort-down'];
        default:
          return ['fas', 'fa-sort'];
      }
    },
    async setQuery() {
      if (this.pageInQuery && this.$route.query.page) {
        this.$router.push({ query: {} });
      }
      await this.reload();
    },
    onWindowResize: throttle(function () {
      this.updatePaginationLimit();
    }, 300),
    updatePaginationLimit() {
      const paginatedTable = this.$refs['paginated-table'];
      if (paginatedTable) {
        const tableWidth = paginatedTable.clientWidth;
        this.paginationLimit = [
          { width: 500, limit: 3 },
          { width: 800, limit: 5 },
          { width: 1000, limit: 9 },
        ].reduce((limit, item) => {
          return item.width > tableWidth ? limit : item.limit;
        }, 1);
      }
    },
    getCssClass(cellData) {
      const cssClass = cellData.cssClass?.split(' ') || '';
      if (cellData.disableClick) cssClass.push('disable-click');
      return cssClass.join(' ');
    },
    setHoverIndex(rowIndex, cellData) {
      if (!cellData.disableClick) {
        this.hoverIndex = rowIndex;
      }
    },
    getPageQuery(page) {
      if (page > 1) return { page };
      else return {};
    },
    getPageFromQuery() {
      return parseInt(this.$route.query.page || 1);
    },
    onRouteChange() {
      if (!this.pageInQuery) return;
      const page = this.getPageFromQuery();
      if (page != this.currentPage) {
        this.onPageSelect(page);
      }
    },
    // table
    async makeRequest(page=1) {
      const query = {
        ...this.query,
        ordering: this.orderingParam,
        page,
      };
      return this.api(query);
    },
    async onPageSelect(page) {
      this.currentPage = page;
      const res = await this.makeRequest(page);
      this.res = res;
      return res;
    },
    async refresh() {
      /*
      CALL WHEN YOU ONLY WANT TO RELOAD THE CURRENT PAGE,
      AND NOT WHEN FILTERS CHANGE OR SIMILAR
      */
      const res = await this.makeRequest(this.currentPage);
      this.res = res;
      return res;
    },
    async reload(page: 1) {
      this.isLoading = true;
      this.currentPage = page || 1;
      this.perPage = 0;
      this.allPages = 0;
      this.res = emptyResponse;
      const res = await this.makeRequest(page);
      this.res = res;
      this.perPage = this.perPageFixed || res.results.length;
      const allPages = res.results.length
        ? Math.ceil(res.count / this.perPage)
        : 0;
        this.allPages = allPages;

        this.isLoading = false;
      return res;
    },
    async orderingBy(ordering: string) {
      if (this.ordering !== ordering) {
        this.ordering = ordering;
        this.orderingDirection = '';
      } else if (this.ordering === ordering) {
        if (this.orderingDirection === '') {
          this.orderingDirection = '-';
        } else {
          this.ordering = null;
        }
      }
      this.refresh();
    },
    setCurrentPage(currentPage) {
      const routePage = this.$route.query.page || 1;
      if (this.pageInQuery) {
        if (routePage != currentPage) {
          const routeName = this.$route.name || undefined;
          this.$router.push({
            name: routeName,
            query: this.getPageQuery(currentPage),
          });
        }
      } else {
        this.onPageSelect(currentPage);
      }
    },
  },
  computed: {
    tableData() {
      if (this.isLoading) return [];

      const tableData = this.res.results.map(result => {
        return this.fields.map((field, fieldIndex) => {
          return {
            cssClass: field.cssClass || '',
            slotData: result,
            slotName: field.name || fieldIndex,
            text: field.key ? get(result, field.key) : null,
            disableClick: field.disableClick,
          };
        });
      });

      return tableData;
    },
    showPagination() {
      return this.allPages > 1
    },
    orderingParam() {
      if (!this.ordering) return null;
      return this.orderingDirection + this.ordering;
    },
    isNoData() {
      return (
        !this.isLoading &&
        !this.res?.results?.length &&
        values(this.query.value).every(isEmpty)
      );
    },
    isNoDataForQuery() {
      return (
        !this.isLoading &&
        !this.res?.results?.length &&
        !values(this.query.value).every(isEmpty)
      );
    },
    currentPageValue: {
      get() {
        return this.currentPage;
      },
      set(value) {
        this.setCurrentPage(value);
      }
    },
  },
  watch: {
    query: 'setQuery',
    // orderingParam: 'setOrderingParam',
    $route: 'onRouteChange',
  },
  async mounted() {

    let initialPage = 1;
    if (this.pageInQuery) {
      initialPage = this.getPageFromQuery();
    }
    try {
      await this.reload(initialPage);
    } catch (e) {
      // noop
      console.error(e);
    }

    this.firstLoad = false;
    window.addEventListener('resize', this.onWindowResize);
  },
  created() {
  },
  updated: function () {
    this.$nextTick(() => {
      this.updatePaginationLimit();
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';

// .table-right-spacer {
//   flex-shrink: 0;
//   width: $table-right-spacing;
// }

.table-wrap {
  overflow-x: auto;
}

tbody td {
  vertical-align: middle;
}

.is-hover {
  td:not(.disable-click) {
    background-color: $light-gray;
  }
}

.ordering--selected {
  color: var(--brand-color, $coral);
}

.cell-shrink {
  width: 0.1%;
  white-space: nowrap;
}

.page-item.active {
  .page-link {
    background-color: $light-gray;
    color: $dark-gray;
    outline: none;
    &:focus {
      outline: none;
      box-shadow: none;
    }
  }
}
.page-link {
  border-radius: 4px;
  padding: 0.75rem 1.3rem;
  margin: 0 0.25rem;
  color: $medium-gray;
  border: 1px solid $medium-gray;
  &:hover {
    color: $dark-gray;
  }
  &:focus {
    outline: none;
    box-shadow: none;
  }
}
</style>

<style lang="scss">
.table-button {
  width: 44px;
}
</style>
